import { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import { Direction } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/enums.pb';
import { SMS } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v1/models.pb';
import { EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import dayjs from 'dayjs';
import { SMSDataV4 } from '@frontend/api-sms-data';
import { useMessageAction } from '@frontend/contact-actions-send-message';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useInboxNavigate } from '@frontend/inbox-navigation';
import { theme } from '@frontend/theme';
import { Text, Avatar, Button, useAlert, NakedButton, SkeletonLoader } from '@frontend/design-system';
import { useGetCitationPersonName } from '../hooks/use-get-citation-person-name';
import { MessageInsightsTagsSection } from './message-insights-tags-section';

interface MessageInsightsBubble {
  locationId: string;
  sourceId?: string;
  onSmsLoaded?: (sms: SMS | null) => void;
}

export const MessageInsightsBubble: FC<MessageInsightsBubble> = ({ locationId, sourceId, onSmsLoaded }) => {
  const { t } = useTranslation('message-insights');
  const { error: errorAlert } = useAlert();
  const [sms, setSms] = useState<SMS | null>(null);
  const fetchGetSMS = SMSDataV4.Queries.useFetchGetUnifiedThreadItem();
  const [isLoading, setIsLoading] = useState(false);
  const messageOutbound = sms?.direction && sms.direction === Direction.DIRECTION_OUTBOUND;

  const { onClick: openMiniChatThread } = useMessageAction();
  const { closeThread, threadParams } = useInboxNavigate();
  const date = sms?.createdAt ? dayjs(sms?.createdAt).format('MMM DD, YYYY, hh mm A') : '';
  const { personName, isLoading: isFetchingPersonName } = useGetCitationPersonName({
    patientId: sms?.personId,
    userId: sms?.updatedBy,
    direction: sms?.direction,
  });

  const onClick = async () => {
    setIsLoading(true);
    if (!sourceId) {
      setIsLoading(false);
      return;
    }

    try {
      const result = await fetchGetSMS({
        itemId: sourceId,
        locationId,
        entityType: EntityType.ENTITY_TYPE_SMS,
      });
      const smsMessage = 'smsMessage' in result.threadItem ? result.threadItem.smsMessage : null;
      if (!smsMessage?.id) {
        setIsLoading(false);
        return;
      }
      if (threadParams?.filter && threadParams?.threadId === smsMessage.threadId) {
        closeThread();
      }
      openMiniChatThread({
        threadGroupId: locationId,
        threadId: smsMessage.threadId,
        focusThreadItemData: {
          id: smsMessage.id,
          createdAt: smsMessage.createdAt,
        },
      });
    } catch (error) {
      errorAlert(t('Unable to open message at this moment'));
    }
    setIsLoading(false);
  };

  useEffect(() => {
    const fetchSMS = async () => {
      try {
        const result = await fetchGetSMS({
          itemId: sourceId || '',
          locationId,
          entityType: EntityType.ENTITY_TYPE_SMS,
        });
        const smsMessage = 'smsMessage' in result.threadItem ? result.threadItem.smsMessage : null;
        if (!('smsMessage' in result.threadItem) || !result.threadItem.smsMessage.id) {
          return null;
        }
        return smsMessage;
      } catch (error) {
        errorAlert(t('Unable to open message at this moment'));
      }
      return null;
    };

    (async () => {
      const smsMessage = await fetchSMS();
      setSms(smsMessage ?? null);
      onSmsLoaded?.(smsMessage ?? null);
    })();
  }, [sourceId, locationId]);

  if (!sms) {
    return (
      <div css={noCitationContainerStyle}>
        <Icon name='message' size={16} />{' '}
        <Text size='large' weight='bold'>
          {t('View Details in Inbox')}
        </Text>
        <Button variant='tertiary' loading={isLoading} css={alignRight} onClick={onClick}>
          {t('View')}
        </Button>
      </div>
    );
  }

  return (
    <>
      <NakedButton css={citationContainerStyle} onClick={onClick}>
        <span>
          <Icon name='inbox' size={16} />
          <Text size='large' weight='bold'>
            {t('Inbox')}
          </Text>
        </span>
        <span css={messageOutbound && reverseFlex} className='message-citation'>
          <SkeletonLoader isLoading={isFetchingPersonName} width={40} height={40} shape='circle'>
            <Avatar
              name={personName}
              isUser={!!sms.updatedBy}
              isAiAssistant={personName === 'AI Receptionist' && sms.direction === Direction.DIRECTION_OUTBOUND}
            />
          </SkeletonLoader>
          {sms?.body && (
            <span css={[messageStyle, messageOutbound && outboundMessageBackground]}>
              <Text size='large'>{sms?.body}</Text>
            </span>
          )}
        </span>
        <span css={messageOutbound && reverseFlex}>
          <SkeletonLoader isLoading={isFetchingPersonName} width={140} height={18}>
            <Text size='medium' weight='bold'>
              {personName}
            </Text>
          </SkeletonLoader>
          <Text color='light' size='small' css={messageOutbound ? alignLeft : alignRight}>
            {date}
          </Text>
        </span>
      </NakedButton>
      <MessageInsightsTagsSection sms={sms} locationId={locationId} />
    </>
  );
};

const citationContainerStyle = css`
  display: flex;
  flex-direction: column;
  padding: ${theme.spacing(2)};
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadius.medium};
  box-shadow: ${theme.shadows.floating};
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  margin-top: ${theme.spacing(2)};
  border-left: 4px solid ${theme.colors.primary30};
  border-radius: ${theme.borderRadius.small};
  gap: ${theme.spacing(1)};
  width: 100%;
  margin-bottom: ${theme.spacing(2)};
  &:hover {
    box-shadow: ${theme.shadows.floating};
  }

  > span {
    display: inherit;
    align-items: center;
    gap: ${theme.spacing(1)};
  }

  .message-citation {
    display: inherit;
    align-items: flex-end;
  }
`;

const messageStyle = css`
  padding: ${theme.spacing(2)};
  background-color: ${theme.colors.neutral5};
  border-radius: ${theme.borderRadius.medium};
  text-align: left;
  flex: 1;
`;

const outboundMessageBackground = css`
  background-color: ${theme.colors.primary5};
`;

const alignRight = css`
  margin-left: auto;
`;

const alignLeft = css`
  margin-right: auto;
`;

const noCitationContainerStyle = css`
  border-left: 4px solid ${theme.colors.primary30};
  border-radius: ${theme.borderRadius.small};
  gap: ${theme.spacing(1)};
  box-shadow: ${theme.shadows.floating};
  padding: ${theme.spacing(2)};
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: ${theme.spacing(2, 0)};
`;

const reverseFlex = css`
  flex-direction: row-reverse;
`;
